import { useQuery } from "@apollo/client";
import { Chip, Skeleton, Stack, Typography } from "@mui/material";
import { gql } from "generated-graphql";
import {
  ReportableWasteCodesQuery,
  ReportableWasteCodesQueryVariables,
} from "generated-graphql/graphql";

import { StepSubHeader } from "../StepSubHeader";

/**
 * All-time union of the facility's reportable federal + state waste codes.
 *
 * Queried with `startDate: null, endDate: null, view: REPORTABLE` so it matches
 * exactly what the 8700-12 snapshot freezes (`gatherRcra870012Content.ts` uses
 * `view: "REPORTABLE"` over an unbounded window) — i.e. "what you see is what
 * you submit". This is intentionally NOT bound to the step's adjustable window
 * control; it always reflects the full reportable history.
 */
const REPORTABLE_WASTE_CODES = gql(`
  query ReportableWasteCodes($facilityId: ID!) {
    facility(id: $facilityId) {
      id
      wasteCodes(startDate: null, endDate: null, view: REPORTABLE) {
        federalWasteCodes
        stateWasteCodes
      }
    }
  }
`);

function CodeList({ title, codes }: { title: string; codes: string[] }) {
  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">{title}</Typography>
      {codes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No {title.toLowerCase()} reported.
        </Typography>
      ) : (
        <Stack direction="row" flexWrap="wrap" gap={0.5}>
          {codes.map((code) => (
            <Chip key={code} label={code} size="small" />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

export function ReportableWasteCodes({ facilityId }: { facilityId: string }) {
  const { data, loading } = useQuery<ReportableWasteCodesQuery, ReportableWasteCodesQueryVariables>(
    REPORTABLE_WASTE_CODES,
    { variables: { facilityId } }
  );

  const federalWasteCodes = data?.facility?.wasteCodes?.federalWasteCodes ?? [];
  const stateWasteCodes = data?.facility?.wasteCodes?.stateWasteCodes ?? [];

  return (
    <Stack spacing={2}>
      <StepSubHeader title="Reportable waste codes" />
      <Typography variant="caption" color="text.secondary">
        All reportable federal and state waste codes for the facility — the same set included in the
        submission. Not affected by the date window above.
      </Typography>
      {loading ? (
        <Skeleton height={120} variant="rounded" />
      ) : (
        <>
          <CodeList title="Federal waste codes" codes={federalWasteCodes} />
          <CodeList title="State waste codes" codes={stateWasteCodes} />
        </>
      )}
    </Stack>
  );
}
